import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Input, Space } from 'antd';
import { LockOutlined, MailOutlined, NumberOutlined } from '@ant-design/icons';
import { useLocation, Link } from 'react-router-dom';
import Hero from 'components/Hero';
import useGlobalContext from 'hooks/useGlobalContext';
import useFormatMessage from 'hooks/useFormatMessage';
import { Mfa } from 'shared/prisma/types';
import { sortMfaMethods } from 'shared/constants/mfa';
import messages from './messages';
import useAuth from '../../hooks/useAuth';

const MFA_METHOD_LABELS = {
  [Mfa.Sms]: 'SMS',
  [Mfa.Email]: 'Email',
  [Mfa.Totp]: 'Authenticator app',
};

const LoginForm = () => {
  const $t = useFormatMessage();
  return (
    <>
      <Form.Item name="email" rules={[{ required: true, message: $t(messages.emailRequired) }]}>
        <Input
          prefix={<MailOutlined className="input-icon" />}
          type="email"
          placeholder={$t(messages.email)}
        />
      </Form.Item>
      <Form.Item name="password" rules={[{ required: true, message: $t(messages.passwordRequired) }]}>
        <Input.Password
          prefix={<LockOutlined className="input-icon" />}
          placeholder={$t(messages.password)}
        />
      </Form.Item>
    </>
  );
};

const MfaForm = ({ mfaMethod, mfaMethods, onResend, onChangeMethod, loading }) => {
  const $t = useFormatMessage();
  const inputRef = useRef();
  const methodMessage = {
    [Mfa.Sms]: messages.mfaSms,
    [Mfa.Email]: messages.mfaEmail,
    [Mfa.Totp]: messages.mfaTotp,
  }[mfaMethod] || messages.mfa;

  const alternativeMethods = sortMfaMethods((mfaMethods || []).filter((method) => method !== mfaMethod));

  useEffect(() => {
    if (loading) return undefined;
    const id = setTimeout(() => inputRef.current?.focus());
    return () => clearTimeout(id);
  }, [mfaMethod, loading]);

  return (
    <>
      <p>{$t(methodMessage)}</p>
      <Form.Item
        name="mfaCode"
        label={$t(messages.mfaCode)}
        rules={[{ required: true, len: 6, message: $t(messages.mfaCodeRequired) }]}
        extra={mfaMethod !== Mfa.Totp && (
          <Button type="link" onClick={onResend} disabled={loading} style={{ padding: 0 }}>
            {$t(messages.resendCode)}
          </Button>
        )}
      >
        <Input
          ref={inputRef}
          autoFocus
          prefix={<NumberOutlined className="input-icon" />}
          placeholder={$t(messages.mfaCode)}
        />
      </Form.Item>
      <Form.Item label={$t(messages.tryDifferentMethod)}>
        <Space wrap>
          {alternativeMethods.map((method) => (
            <Button key={method} onClick={() => onChangeMethod(method)} disabled={loading}>
              {MFA_METHOD_LABELS[method] || method}
            </Button>
          ))}
        </Space>
      </Form.Item>
    </>
  );
};

const Login = () => {
  const $t = useFormatMessage();
  const [loading, setLoading] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [mfaMethod, setMfaMethod] = useState();
  const [mfaMethods, setMfaMethods] = useState([]);
  const location = useLocation();
  const { login } = useAuth();
  const { modal } = useGlobalContext();
  const [form] = Form.useForm();

  useEffect(() => {
    const state = location?.state;
    if (state?.email) {
      form.setFieldsValue({ email: state.email });
    }
  }, [location?.state, form]);

  const handleMfaResponse = (res) => {
    if (res?.mfa) {
      setMfa(true);
      setMfaMethod(res.mfa);
      setMfaMethods(sortMfaMethods(res.mfaMethods || [res.mfa]));
    }
  };

  const onSubmit = async () => {
    const values = form.getFieldsValue(true);
    setLoading(true);
    try {
      const res = await login(
        values.email,
        values.password,
        values.mfaCode,
        location?.state?.referrer,
        mfaMethod,
      );
      handleMfaResponse(res);
    } catch (e) {
      modal.error({ content: messages[e.message] ? $t(messages[e.message]) : e.message, centered: true });
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    const values = form.getFieldsValue(true);
    setLoading(true);
    try {
      const res = await login(
        values.email,
        values.password,
        undefined,
        location?.state?.referrer,
        mfaMethod,
      );
      handleMfaResponse(res);
      modal.success({ content: $t(messages.codeResent), centered: true });
    } catch (e) {
      modal.error({ content: messages[e.message] ? $t(messages[e.message]) : e.message, centered: true });
    } finally {
      setLoading(false);
    }
  };

  const onChangeMethod = async (method) => {
    const values = form.getFieldsValue(true);
    setMfaMethod(method);
    form.setFieldsValue({ mfaCode: undefined });
    setLoading(true);
    try {
      const res = await login(
        values.email,
        values.password,
        undefined,
        location?.state?.referrer,
        method,
      );
      handleMfaResponse(res);
    } catch (e) {
      modal.error({ content: messages[e.message] ? $t(messages[e.message]) : e.message, centered: true });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Hero branded>
      <Card>
        <Form onFinish={onSubmit} layout="vertical" form={form} validateTrigger="onSubmit">
          {mfa ? (
            <MfaForm
              mfaMethod={mfaMethod}
              mfaMethods={mfaMethods}
              onResend={onResend}
              onChangeMethod={onChangeMethod}
              loading={loading}
            />
          ) : (
            <LoginForm />
          )}
          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={loading}>
              {$t(messages.login)}
            </Button>
          </Form.Item>
          {!mfa && (
            <Link to="/forgot-password">
              {$t(messages.forgotPassword)}
            </Link>
          )}
        </Form>
      </Card>
    </Hero>
  );
};

export default Login;
